import { useTransactionStatus } from "Api/Queries/Transactions/useTransactionStatus";
import { useBankReturnRelay } from "Hooks/Transactions/useBankReturnRelay";
import { useGatewayReturnRedirect } from "Hooks/Transactions/useGatewayReturnRedirect";
import { useTransactionStatusSignalR } from "Hooks/Transactions/useTransactionStatusSignalR";
import { FailedTransactionInfo } from "Components/Transactions/Anonymous/FailedTransactionInfo";
import { LoadingSkeleton } from "Components/Transactions/Anonymous/LoadingSkeleton";
import { SuccessfulTransactionInfo } from "Components/Transactions/Anonymous/SuccessfulTransactionInfo/SuccessfulTransactionInfo";
import { TransactionStatus } from "Infrastructure/Api/Api";

type Props = {
  transactionPublicID: string;
};

export const TransactionDetail = ({ transactionPublicID }: Props) => {
  const { data, isLoading, isError } =
    useTransactionStatus(transactionPublicID);

  // Disable SignalR for anonymous users (no connection established)
  useTransactionStatusSignalR({
    publicID: transactionPublicID,
    enabled: false,
  });

  // The anonymous payer lands here straight from the bank too (QR / PayPoint / gateway checkout) — hand the
  // bank's redirect parameters to the backend, otherwise a rejection at e.g. Moneta is never recorded.
  useBankReturnRelay({ publicID: transactionPublicID });

  // E-commerce gateway payments: send the customer back to the e-shop once finished
  useGatewayReturnRedirect({
    transactionPublicID,
    transactionStatus: data?.transactionStatus,
    returnUrl: data?.returnUrl,
  });

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const hasTransactionFailed =
    !data || isError || data.transactionStatus === TransactionStatus.Fail;

  if (hasTransactionFailed) {
    return <FailedTransactionInfo failureReason={data?.failureReason} />;
  }

  // Polling stops on Cancelled; without this branch the anonymous page would show the processing skeleton forever.
  if (data.transactionStatus === TransactionStatus.Cancelled) {
    return (
      <FailedTransactionInfo failureReason={data.failureReason} isCancelled />
    );
  }

  if (data.transactionStatus !== TransactionStatus.Success) {
    return <LoadingSkeleton variant="processingTransaction" />;
  }

  return (
    <SuccessfulTransactionInfo transactionPublicID={transactionPublicID} />
  );
};
